import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://flood-and-fire-pros.direct';
const LOGO_URL = `${SITE_URL}/wp-content/uploads/2025/07/FFP_vector_logo_blue_orange_gold_professional-e1751729796397.png`;

const StructuredData = ({ type, data = {} }) => {
  let structuredData = {};

  switch (type) {
    case 'WebSite':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Flood & Fire Pros",
        "url": SITE_URL,
        "description": "Connect with certified restoration professionals for water damage, fire damage, and emergency restoration services.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${SITE_URL}/results/{search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      };
      break;

    case 'Organization':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Flood & Fire Pros",
        "url": SITE_URL,
        "logo": LOGO_URL,
        "description": "Premium restoration professionals marketplace connecting property owners with certified water damage, fire damage, and mold remediation specialists.",
        "areaServed": ["Los Angeles County", "Inland Empire", "Southern California"],
        "serviceType": ["Water Damage Restoration", "Fire Damage Restoration", "Mold Remediation", "Emergency Restoration"],
        ...data
      };
      break;

    case 'LocalBusiness':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Flood & Fire Pros",
        "image": LOGO_URL,
        "description": "24/7 emergency restoration services connecting homeowners with certified professionals for water, fire, and mold damage restoration.",
        "url": SITE_URL,
        "telephone": "24/7 Emergency Response",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "CA",
          "addressCountry": "US"
        },
        "areaServed": {
          "@type": "State",
          "name": "California"
        },
        "openingHours": "Mo-Su 00:00-23:59",
        ...data
      };
      break;

    // Contractor profile schema - combines LocalBusiness + ProfessionalService
    case 'Contractor':
      structuredData = {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "ProfessionalService"],
        "name": data.companyName || "Restoration Contractor",
        "image": data.logo || LOGO_URL,
        "description": data.description || "Professional restoration services",
        "url": data.profileUrl || SITE_URL,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": data.city || "",
          "addressRegion": data.state || "CA",
          "postalCode": data.zipCode || "",
          "addressCountry": "US"
        },
        "geo": data.coordinates ? {
          "@type": "GeoCoordinates",
          "latitude": data.coordinates.lat,
          "longitude": data.coordinates.lng
        } : undefined,
        "areaServed": (data.serviceAreas || []).map(zip => ({
          "@type": "PostalAddress",
          "postalCode": zip
        })),
        "openingHoursSpecification": data.emergencyAvailable ? {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        } : undefined,
        "priceRange": "$$",
        "aggregateRating": data.rating ? {
          "@type": "AggregateRating",
          "ratingValue": data.rating,
          "reviewCount": data.reviewCount || 0,
          "bestRating": 5,
          "worstRating": 1
        } : undefined,
        "hasCredential": (data.certifications || []).map(cert => ({
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Professional Certification",
          "name": cert
        })),
        "knowsAbout": data.specialties || [],
        "isAcceptingNewCustomers": true,
        "paymentAccepted": "Cash, Credit Card, Insurance",
        "currenciesAccepted": "USD"
      };
      // Clean undefined values
      structuredData = JSON.parse(JSON.stringify(structuredData));
      break;

    // Service page schema
    case 'Service':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": data.serviceType || "Restoration Service",
        "name": data.name || "Professional Restoration",
        "description": data.description || "Professional restoration services for property damage",
        "provider": {
          "@type": "Organization",
          "name": "Flood & Fire Pros",
          "url": SITE_URL
        },
        "areaServed": {
          "@type": "State",
          "name": "California"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": data.catalogName || "Restoration Services",
          "itemListElement": (data.services || []).map((service, index) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": service.name,
              "description": service.description
            },
            "position": index + 1
          }))
        },
        "availableChannel": {
          "@type": "ServiceChannel",
          "serviceUrl": `${SITE_URL}/get-help-now`,
          "serviceSmsNumber": "Available via platform"
        }
      };
      break;

    // FAQ page schema
    case 'FAQPage':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": (data.faqs || []).map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      break;

    // Breadcrumb schema for navigation
    case 'BreadcrumbList':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": (data.items || []).map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url ? `${SITE_URL}${item.url}` : undefined
        }))
      };
      break;

    // Article/Blog schema
    case 'Article':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": data.title || "",
        "description": data.description || "",
        "image": data.image || LOGO_URL,
        "author": {
          "@type": "Organization",
          "name": "Flood & Fire Pros"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Flood & Fire Pros",
          "logo": {
            "@type": "ImageObject",
            "url": LOGO_URL
          }
        },
        "datePublished": data.datePublished || new Date().toISOString(),
        "dateModified": data.dateModified || new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": data.url || SITE_URL
        }
      };
      break;

    // HowTo schema for guides
    case 'HowTo':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": data.title || "How To Guide",
        "description": data.description || "",
        "step": (data.steps || []).map((step, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": step.name,
          "text": step.text,
          "image": step.image
        })),
        "totalTime": data.totalTime || "PT30M"
      };
      break;

    default:
      structuredData = { "@context": "https://schema.org", ...data };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default StructuredData;